// Mirrors coinsfly-backend src/common/modules/invoice-payments/types.

export enum EInvoicePaymentStatus {
  UNDER_REVIEW = 'under_review',
  MORE_DOCUMENTS_REQUESTED = 'more_documents_requested',
  APPROVED = 'approved',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  FAILED = 'failed',
}

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  [EInvoicePaymentStatus.UNDER_REVIEW]: 'Under Review',
  [EInvoicePaymentStatus.MORE_DOCUMENTS_REQUESTED]: 'More Documents',
  [EInvoicePaymentStatus.APPROVED]: 'Approved (Pending Execution)',
  [EInvoicePaymentStatus.PROCESSING]: 'Processing',
  [EInvoicePaymentStatus.COMPLETED]: 'Completed',
  [EInvoicePaymentStatus.REJECTED]: 'Rejected',
  [EInvoicePaymentStatus.CANCELLED]: 'Cancelled',
  [EInvoicePaymentStatus.FAILED]: 'Failed',
};

export enum EInvoiceDocumentType {
  INVOICE = 'invoice',
  AGREEMENT = 'agreement',
  SOURCE_OF_FUNDS = 'source_of_funds',
  PROOF_OF_PAYMENT = 'proof_of_payment',
  ADDITIONAL = 'additional',
}

export interface IInvoicePaymentListItem {
  id: string;
  invoicePaymentNumber: string;
  createdAt: string;
  clientEmail: string;
  clientUid: string;
  clientName?: string;
  beneficiaryName?: string;
  beneficiaryCountry: string;
  invoiceNumber: string;
  fiatAmount: string;
  fiatCurrency: string;
  cryptoAmount: string;
  cryptoAsset: string;
  rail: string;
  status: EInvoicePaymentStatus;
  riskFlags: string[];
  assignedReviewer?: string | null;
  updatedAt: string;
}

export interface IInvoicePaymentList {
  total: number;
  page: number;
  limit: number;
  items: IInvoicePaymentListItem[];
}

export interface IInvoiceDocument {
  id: string;
  document_type: EInvoiceDocumentType;
  file_name: string;
  file_mime_type: string;
  file_size: number;
  created_at: string;
}

export interface IInvoiceBeneficiary {
  id: string;
  beneficiary_name: string;
  beneficiary_country: string;
  address_number: string;
  street: string;
  city: string;
  town_state_province?: string | null;
  zip_postcode: string;
  registration_number: string;
  website_url?: string | null;
  transfer_reason: string;
}

export interface IInvoiceBankDetails {
  id: string;
  bank_name?: string;
  bank_country?: string;
  bank_address?: string | null;
  iban?: string | null;
  account_number?: string | null;
  sort_code?: string | null;
  routing_number?: string | null;
  bic_swift?: string | null;
}

export interface IInvoicePaymentDetail {
  id: string;
  invoice_payment_number: string;
  user_id: string;
  client_email: string;
  status: EInvoicePaymentStatus;
  payment_purpose: string;
  beneficiary_sector: string;
  beneficiary_country: string;
  fiat_currency: string;
  fiat_amount: string;
  crypto_asset: string;
  crypto_amount_reserved: string;
  fee_percentage: string;
  fee_amount_crypto: string;
  exchange_rate: string;
  eur_equivalent: string;
  rail: string;
  invoice_number: string;
  payment_reference: string;
  risk_flags: string[];
  rejection_reason?: string | null;
  more_documents_note?: string | null;
  failure_reason?: string | null;
  assigned_reviewer?: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  cancelled_at?: string | null;
  processing_started_at?: string | null;
  completed_at?: string | null;
  failed_at?: string | null;
  balance_deducted_at?: string | null;
  balance_refunded_at?: string | null;
  balance_settled_at?: string | null;
  transfi_order_id?: string | null;
  transfi_quote_id?: string | null;
  transfi_conversion_rate?: string | null;
  transfi_status?: string | null;
  transfi_failure_code?: string | null;
  transfi_failure_message?: string | null;
  transfi_invoice_id?: string | null;
  transfi_kyb_url?: string | null;
  executed_at?: string | null;
  created_at: string;
  updated_at: string;
  beneficiary?: IInvoiceBeneficiary;
  bank_details?: IInvoiceBankDetails;
  documents?: IInvoiceDocument[];
  client?: {
    id: string;
    coinsFlyId: string;
    name?: string;
    email?: string;
    clientType?: string;
    kybStatus?: string;
    accountStatus?: string;
    invoicePaymentEnabled?: boolean;
    transfiUserId?: string | null;
    transfiKybStatus?: string | null;
  } | null;
}

// --- Config entities ---

export enum EConfigStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface IInvoiceSenderAccount {
  id: string;
  sender_id: string;
  currency: string;
  status: EConfigStatus;
  bank_name: string;
  bank_country: string;
  bank_address: string;
  iban?: string | null;
  account_number?: string | null;
  sort_code?: string | null;
  routing_number?: string | null;
  bic_swift?: string | null;
  supported_rails: string[];
}

export interface IInvoiceSender {
  id: string;
  name: string;
  status: EConfigStatus;
  country: string;
  address: string;
  registration_number: string;
  website_url?: string | null;
  supported_client_types: string[];
  supported_payment_purposes: string[];
  supported_sector_categories?: string[] | null;
  supported_beneficiary_countries?: string[] | null;
  supported_bank_countries?: string[] | null;
  min_amount?: string | null;
  max_amount?: string | null;
  internal_notes?: string | null;
  accounts?: IInvoiceSenderAccount[];
}

export interface IInvoiceFeeRule {
  id: string;
  sender_id?: string | null;
  currency: string;
  rail: string;
  amount_min: string;
  amount_max?: string | null;
  fee_percentage: string;
  status: EConfigStatus;
}

export interface IInvoiceRouteRule {
  id: string;
  currency: string;
  beneficiary_country?: string | null;
  beneficiary_bank_country?: string | null;
  bank_country_group?: string | null;
  sender_id?: string | null;
  rail: string;
  priority: number;
  min_amount?: string | null;
  max_amount?: string | null;
  required_fields: string[];
  status: EConfigStatus;
}

export interface IInvoiceCountry {
  id: string;
  name: string;
  iso_code?: string | null;
  list_type: 'applicant' | 'beneficiary';
  status: 'supported' | 'restricted' | 'disabled';
  enhanced_review: boolean;
}

export interface IInvoiceSector {
  id: string;
  category: string;
  subcategory: string;
  risk_tier: 'low_risk' | 'medium_risk' | 'high_risk' | 'prohibited';
}

export interface IInvoiceSectorList {
  items: IInvoiceSector[];
  total: number;
  page: number;
  limit: number;
}

export interface IInvoiceSettings {
  id: string;
  min_amount_eur: string;
  max_amount_eur: string;
  agreement_threshold_eur: string;
  sof_threshold_eur: string;
  quote_validity_seconds: number;
  inactivity_timeout_seconds: number;
}

export interface IInvoiceConfigMeta {
  rails: string[];
  bankCountryGroups: string[];
  riskTiers: string[];
  countryListTypes: string[];
}
